import { api } from '../utils/api';

export interface ChatMessage {
  id: string;
  role: 'user' | 'assistant' | 'system';
  content: string;
  timestamp: string;
}

export interface ChatResponse {
  success: boolean;
  userMessage: ChatMessage;
  assistantMessage: ChatMessage;
  usage: {
    promptTokens: number;
    completionTokens: number;
    totalTokens: number;
  };
}

export interface StreamChunk {
  chunk?: string;
  done: boolean;
  messageId?: string;
  duration?: number;
  error?: string;
}

export class ChatService {
  /**
   * Send a message and get AI response (non-streaming)
   */
  async sendMessage(conversationId: string, content: string): Promise<ChatResponse> {
    const response = await api.post(`/chat/${conversationId}`, { content });
    return response.data;
  }

  /**
   * Send a message and stream AI response
   * Returns an async generator that yields chunks as they arrive
   */
  async *streamMessage(
    conversationId: string,
    content: string
  ): AsyncGenerator<StreamChunk, void, unknown> {
    const token = localStorage.getItem('accessToken');
    const baseUrl = api.defaults.baseURL || '';

    const response = await fetch(`${baseUrl}/chat/${conversationId}/stream`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ content }),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Failed to stream chat');
    }

    if (!response.body) {
      throw new Error('Response body is null');
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';

    try {
      while (true) {
        const { done, value } = await reader.read();

        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');

        // Keep the last incomplete line in the buffer
        buffer = lines.pop() || '';

        for (const line of lines) {
          if (line.startsWith('data: ')) {
            const data = line.substring(6).trim();
            if (data) {
              try {
                const chunk: StreamChunk = JSON.parse(data);
                yield chunk;

                if (chunk.done) {
                  return;
                }
              } catch (e) {
                console.error('Failed to parse SSE data:', data, e);
              }
            }
          }
        }
      }
    } finally {
      reader.releaseLock();
    }
  }

  /**
   * Check if chat service is available
   */
  async checkAvailability(): Promise<boolean> {
    try {
      const response = await api.get('/health');
      return response.data.services?.openai === 'healthy';
    } catch (error) {
      console.error('Failed to check chat availability:', error);
      return false;
    }
  }

  /**
   * Get conversation usage stats
   */
  async getConversationUsage(conversationId: string): Promise<{
    totalTokens: number;
    totalCost: number;
    messageCount: number;
  }> {
    const response = await api.get(`/usage/conversation/${conversationId}`);
    return response.data;
  }

  /**
   * Get user usage stats
   */
  async getUserUsage(days: number = 30): Promise<{
    totalTokens: number;
    totalCost: number;
    requestCount: number;
    byModel: Record<string, { tokens: number; cost: number; requests: number }>;
  }> {
    const response = await api.get(`/usage/user?days=${days}`);
    return response.data;
  }

  /**
   * Estimate cost for a message
   */
  estimateCost(text: string, model: string = 'gpt-4-turbo-preview'): number {
    // Rough estimate: 1 token ≈ 4 characters
    const inputTokens = Math.ceil(text.length / 4);
    const expectedOutputTokens = 500; // Average response length

    const pricing: Record<string, { input: number; output: number }> = {
      'gpt-4-turbo-preview': { input: 0.01, output: 0.03 },
      'gpt-4': { input: 0.03, output: 0.06 },
      'gpt-3.5-turbo': { input: 0.0015, output: 0.002 },
    };

    const modelPricing = pricing[model] || pricing['gpt-3.5-turbo'];
    const inputCost = (inputTokens / 1000) * modelPricing.input;
    const outputCost = (expectedOutputTokens / 1000) * modelPricing.output;

    return inputCost + outputCost;
  }
}

// Export singleton instance
export const chatService = new ChatService();
